import createCustomGameImg from '../../../assets/images/SupportPage/create-custom-game.png'
import editDeleteCustomGameImg from '../../../assets/images/SupportPage/edit-delete-custom-game.png'
import editDeleteIdeaImg from '../../../assets/images/SupportPage/edit-delete-idea.png'
import inviteCustomGameImg from '../../../assets/images/SupportPage/invite-custom-game.png'
import loginImg from '../../../assets/images/SupportPage/login.png'
import shareGameImg from '../../../assets/images/SupportPage/share-game.png'
import submitIdeasTabImg from '../../../assets/images/SupportPage/submit-ideas-tab.png'
import uploadExportIdeasImg from '../../../assets/images/SupportPage/upload-export-ideas.png'
import {
	battleShipsContent,
	buildingBlocksContent,
	dressUpContent,
	leapFrogContent,
	musicalChairsContent,
	oppositeDayContent,
	roundRobinContent,
	simonSaysContent,
	teeterTotterContent,
} from './FAQGameContent'

export enum FAQCategories {
	Games = 'Games',
	// eslint-disable-next-line typescript-sort-keys/string-enum
	CrossGameFunctionality = 'Cross-Game Functionality',
	CustomGames = 'Custom Games',
	General = 'General',
	Security = 'Security',
	UserAccounts = 'User Accounts',
}

export type FAQDetail = {
	content: string
	imageURL?: string
	title: string
}

export const FAQCategoryDetails: Record<FAQCategories, FAQDetail[]> = {
	[FAQCategories.CrossGameFunctionality]: [
		{
			title: 'How do I know which games to play and when?',
			content: `Follow the home page game sections and descriptions to find the game(s) best suited for your needs. Many workshops involve playing at least 1 game from each of the 3 sections sequentially to maximize the diversity of perspectives on the problem you’re solving. Try out a few games and see which resonate best, and feel free to reach out to our team at: brainstorm@slalom.com if you would like to work with a professionally certified Brainstorm facilitator.`,
		},
		{
			title: 'How do I view submitted ideas?',
			content: `Click the “Submitted Ideas” tab in the header bar at the top of the site (or from the hamburger menu on mobile).`,
			imageURL: submitIdeasTabImg,
		},
		{
			title: 'Can I upload and/or export submitted ideas?',
			content: `Yes, you can upload ideas by clicking the “Upload Ideas” button on the Submitted Ideas page and following the on screen prompts. You can export submitted ideas by “Export Ideas” button on the Submitted Ideas page and following the on screen prompts to export as either a .CSV spreadsheet or a Miro board.`,
			imageURL: uploadExportIdeasImg,
		},
		{
			title: 'How do I edit or delete a submitted idea?',
			content: `From the Submitted Ideas page, click on the pencil icon of a specific idea to edit the content of the submission. Press “Save Changes” at the bottom of the edit modal to update the idea submission with your edits. From the Submitted Ideas page, click on the trash can icon of a specific idea to delete the submission. Press “Delete” on the modal that appears to confirm you want to delete the submission. You cannot undo a deleted idea submission.`,
			imageURL: editDeleteIdeaImg,
		},
		{
			title: 'How many ideas can I submit?',
			content: `There are no character limits on an individual idea submission, and no limits on total ideas you are able to submit.`,
		},
		{
			title: 'Can I save my game and come back later?',
			content: `If you are playing a standard game (accessed from the home page), you can save the innovation topic of your game by pressing the “Share Game” button on page 2 of any game. However, you cannot save the specific instance of GenAI prompts, and any ideas you’ve documented in your ideation section will only be saved if you press “Submit Ideas”.`,
			imageURL: shareGameImg,
		},
	],
	[FAQCategories.CustomGames]: [
		{
			title: 'What is a custom game?',
			content: `A custom game enables you to preset the innovation topic, set a deadline, and share the game with others so you can see their idea submissions.`,
		},
		{
			title: 'How do I create a custom game?',
			content: `
                Click the “Custom Games” tab in the header bar at the top of the site (or from the hamburger menu on tablet/mobile). Click the “Create Custom Game” button and complete the fields in the modal that appears and press “Publish Game” to create the game
            
                If you do not see the “Create Custom Game” button, your account doesn’t have full platform access. Contact our team at brainstorm@slalom.com to get full access.
            `,
			imageURL: createCustomGameImg,
		},
		{
			title: 'How do I invite others to a custom game?',
			content:
				'After creating a game, on the Custom Games page select the person icon in the bottom right hand corner of the custom game card reflecting the game name you want to share. Then, enter the emails you would like to invite to the game and press the “Send” button.',
			imageURL: inviteCustomGameImg,
		},
		{
			title: 'How do I edit or delete a custom game?',
			content: `On the Custom Games page, select the 3 dots in the top right corner of any existing custom game to access the dropdown options to edit or delete a game. Follow the prompts for either option.`,
			imageURL: editDeleteCustomGameImg,
		},
		{
			title: 'If I delete a game, will the ideas submitted to that game also be deleted?',
			content: `No, the ideas submitted to a deleted custom game will still be visible on the submitted ideas page.`,
		},
		{
			title: 'What happens when my custom game expires?',
			content: `You can edit the custom game to extend the deadline, allow it to remain expired to still the game name as a filter on the submitted ideas page, or delete the game.`,
		},
		{
			title: 'How many people can I invite to my custom game?',
			content: `-	As many as you would like!`,
		},
	],
	[FAQCategories.Games]: [
		{
			title: 'Building Blocks',
			content: buildingBlocksContent,
		},
		{
			title: 'Opposite Day',
			content: oppositeDayContent,
		},
		{
			title: 'Dress Up',
			content: dressUpContent,
		},
		{
			title: 'Simon Says',
			content: simonSaysContent,
		},
		{
			title: 'Battleships',
			content: battleShipsContent,
		},
		{
			title: 'Leap Frog',
			content: leapFrogContent,
		},
		{
			title: 'Teeter Totter',
			content: teeterTotterContent,
		},
		{
			title: 'Musical Chairs',
			content: musicalChairsContent,
		},
		{
			title: 'Round Robin',
			content: roundRobinContent,
		},
	],
	[FAQCategories.General]: [
		{
			title: 'What is Brainstorm?',
			content:
				'Brainstorm is a ideation accelerator. It combines GenAI-powered and shareable workshop facilitation techniques, ideas, idea synthesis, and gamification to help humans create more ideas, faster.',
		},
		{
			title: 'Does Brainstorm work on mobile and desktop?',
			content:
				'Yes it does, and on tablets as well! As long as you have internet, any moment on any device can be an opportunity to Brainstorm.',
		},
		{
			title: 'What are the main use cases for Brainstorm?',
			content:
				'Anytime you need to think creatively about a business problem. Brainstorm does not work as well for solving technical problems, but if you have a sales, marketing, customer experience, product, or operations problem, Brainstorm should have a game for you!',
		},
		{
			title: 'How does Brainstorm compare to infinite whiteboards or standard LLMs?',
			content: `What sets us apart is the combination of our facilitation techniques + GenAI to prompt human creativity. Infinite whiteboards are collaboration tools, while Brainstorm is an ideation tool. Both are essential, but ideation generally precedes collaboration. While whiteboards and sticky notes are great at documenting ideas, they don’t prompt novel new ones.
                \nWith Brainstorm, you never need to start with a blank screen or whiteboard again. While LLMs could be considered ideation tools, they are designed to deliver the statistically most likely response - which tends to translate to the statistically most average ideas without careful prompting. Reviewing lists of ideas from what the known world has already documented online can help users achieve competitive parity, but it is less likely to drive lateral thinking and novel concepts.
                `,
		},
		{
			title: 'Can I submit feature requests?',
			content:
				'Yes, please do! Email us at: brainstorm@slalom.com for any features you would like to see added.',
		},
	],
	[FAQCategories.Security]: [
		{
			title: 'Are my ideas private?',
			content:
				'Yes, they are. You are the only one who can see your submitted ideas to standard games started from the home page. If you submit ideas to a custom game you were invited to, the creator of the custom game will also be able to see your submitted ideas.',
		},
		{
			title: 'Is any data from Brainstorm “training” any LLM dataset?',
			content:
				'No. Everything you submit to Brainstorm is protected by our enterprise-grade security, and our GenAI functionality comes from Azure OpenAI, which is a private LLM that does not feed any data into any private or public learning models.',
		},
		{
			title: 'Is the AI pre-trained?',
			content: `We leverage prompt engineering to tailor responses based on our various facilitation techniques. We do not supplement Brainstorm’s GenAI with any proprietary data, so it is pulling from data publicly available on the internet when providing facilitation techniques and ideas on gameplay pages.`,
		},
		{
			title: 'Is it possible to train Brainstorm’s GenAI on my organization’s proprietary data?',
			content:
				'Yes, our team can work with you to get a copy of the Brainstorm code into your enterprise software environment and configure it to securely connect to your data.',
		},
		{
			title: 'What are the AI limitations?',
			content:
				'Brainstorm’s GenAI is not designed to provide perfectly tailored and novel solutions to niche or company-specific problems. Rather, Brainstorm uses GenAI to prompt, or inspire, human creativity with starting points for ideation.',
		},
	],
	[FAQCategories.UserAccounts]: [
		{
			title: 'How do I create an account?',
			content: `
                Reach out to our team at: brainstorm@slalom.com to discuss platform access; presently, access to Brainstorm is only offered in conjunction with Slalom services.

                If you are engaged with Slalom services and are invited to a new custom game on Brainstorm, you will receive 2 emails - one with a temporary password and one with the game invite. To get setup:

                    - Click the new game invite link in the email with the subject: “Brainstorm Software Game Invitation”\n
                    - Click the ’Sign Up’ button at the bottom of the login modal\n
                    - Enter the temporary password from the email you should have just received with the subject: “Your new Brainstorm account”\n
                    - Fill out the other fields in the signup modal and click the “Create Account” button
            `,
		},
		{
			title: 'How do I login/logout?',
			content:
				'Once granted access, to login, search www.brainstorm.slalom.com in the browser of your choice and click any button the platform and the login modal will appear. To logout, hover over your name in the top right of the header bar and select the “Logout” dropdown to logout (on tablet/mobile, select the hamburger menu in the top right of the header bar and select the “Logout” option).',
			imageURL: loginImg,
		},
		{
			title: 'How do I edit/delete my account information?',
			content:
				'When you’re logged in, hover over your name in the top right of the header bar and select the “Profile” dropdown to access your profile page (on tablet/mobile, select the hamburger menu in the top right of the header bar and select the “Profile” option). From the profile page you can edit your password, email, or delete your account.',
		},
		{
			title: 'How do I reset/recover my password?',
			content:
				'From the login modal, select the “Forgot Password” option, enter the email associated with your account, then navigate to that email account to receive a temporary password email. Enter the temporary password you are emailed and a new password on the Brainstorm platform and press the “Change Password” button to reset your password.',
		},
	],
}
